import React, { forwardRef } from 'react';
import cx from 'classnames';

import styles from './Button.module.scss';

export type ButtonVariant = 'solid' | 'outline' | 'text' | 'icon';
export type ButtonBorderWidth = 'thin' | 'bold' | 'bolder';
export type ButtonBorderColor = 'light' | 'white' | 'red';
export type ButtonBackgroundColor = 'red' | 'red2' | 'green2';
export type ButtonSize = 'xs' | 'sm' | 'lg';
export type ButtonRounded = 'xs' | 'sm' | 'lg';
export type ButtonColor = 'white' | 'red' | 'blue' | 'green';
export type ButtonCircle = 'sm' | 'lg';

export type ButtonProps = Pick<
  React.HTMLProps<HTMLButtonElement>,
  'children' | 'className' | 'disabled' | 'onClick'
> & {
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariant;
  size?: ButtonSize;
  rounded?: ButtonRounded;
  color?: ButtonColor;
  block?: boolean;
  active?: boolean;
  borderWidth?: ButtonBorderWidth;
  circle?: ButtonCircle;
  borderColor?: ButtonBorderColor;
  backgroundColor?: ButtonBackgroundColor;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      type = 'button',
      variant = 'outline',
      size,
      rounded,
      color = 'white',
      borderWidth = 'thin',
      borderColor = 'light',
      backgroundColor,
      block = false,
      active = false,
      circle,
      ...buttonProps
    },
    ref
  ): JSX.Element => (
    <button
      ref={ref}
      className={cx(className, styles.container, {
        [styles[`size--${size}`]]: size,
        [styles[`circle__size--${circle}`]]: circle,
        [styles[`rounded--${rounded}`]]: rounded,
        [styles[`color--${color}`]]: color,
        [styles[`background__color--${backgroundColor}`]]: backgroundColor,
        [styles[`variant--${variant}`]]: variant,
        [styles[`border__width--${borderWidth}`]]: borderWidth,
        [styles[`border__color--${borderColor}`]]: borderColor,
        [styles.block]: block,
        [styles.active]: active
      })}
      type={type}
      {...buttonProps}
    >
      {children}
    </button>
  )
);

const MemoizedButton = React.memo(Button);

export default MemoizedButton;
